import "./QuoteIntentionalLoader.scss";

import { FC, useContext, useEffect, useState } from "react";

import lottieImage from "assets/lottie/plant_loader.json";
import Lottie from "components/Lottie/Lottie";
import Text from "components/Text/Text";
import { ButtonFormItem } from "config";
import { DynamicFormContext } from "containers/DynamicForm/DynamicFormContext";
import { useButtonAction } from "containers/DynamicForm/useButtonAction";
import { useModal, useTranslation } from "modules";

import { motion, AnimatePresence, Variants } from "framer-motion";

const LOADER_TIMER_LIMIT = 12_000;

const textVariants: Variants = {
  enter: { opacity: 0, y: -20 },
  appear: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
  exit: { opacity: 0, y: 20, transition: { duration: 0.4, ease: "easeIn" } },
};

const QuoteIntentionalLoader: FC = () => {
  // HOOKS
  const { t } = useTranslation();
  const { dynamicForm } = useContext(DynamicFormContext);
  const openModal = useModal();

  // LOCAL STATE
  const [textIndex, setTextIndex] = useState(0);

  // LOCAL DERIVED VARIABLES
  const underwritingQuoteLoaderButtonItem = dynamicForm.items.find(
    dynamicFormItem => dynamicFormItem.key === "intentional-loader-button"
  ) as ButtonFormItem;
  const underwritingQuoteLoaderButtonActions = useButtonAction(
    underwritingQuoteLoaderButtonItem?.content?.actions || []
  );

  const textList = [
    t(
      "underwriting-loader.text.first",
      "Good things take time—we're planting the seeds of your coverage."
    ),
    t(
      "underwriting-loader.text.second",
      "Your protection is almost fully grown—just wrapping up the final steps."
    ),
  ];

  useEffect(() => {
    const intervalDelay = LOADER_TIMER_LIMIT / textList.length;

    const textLoopInterval = setInterval(() => {
      setTextIndex(i => (i + 1) % textList.length);
    }, intervalDelay);

    const dynamicFormActionsTimeout = setTimeout(async () => {
      try {
        await underwritingQuoteLoaderButtonActions({});
      } catch (error) {
        openModal("GeneralErrorModal", { error });
      }
    }, LOADER_TIMER_LIMIT);

    return () => {
      clearInterval(textLoopInterval);
      clearTimeout(dynamicFormActionsTimeout);
    };
  }, []); //eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="underwriting-loader__container">
      <Lottie className="underwriting-loader__image" loop={false} lottieData={lottieImage} />

      <div className="underwriting-loader__content">
        <AnimatePresence mode="wait">
          <motion.div
            className="underwriting-loader__text-wrapper"
            key={textIndex}
            variants={textVariants}
            initial="enter"
            animate="appear"
            exit="exit">
            <Text tag="p3" className="underwriting-loader__text" text={textList[textIndex]} />
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default QuoteIntentionalLoader;
